import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Smartphone, Monitor, Download, Share, Plus, ExternalLink, Sparkles, X } from "lucide-react";

// Stored value is a non-sensitive Unix-ms timestamp (Date.now() at dismissal)
// used to throttle the install nudge for DISMISS_DAYS. No auth/PII here.
const DISMISS_KEY = "renviro-install-dismissed-at";
const DISMISS_DAYS = 7;

function isStandalone() {
  if (typeof window === "undefined") return false;
  if (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) return true;
  if (window.navigator.standalone) return true; // iOS Safari
  return false;
}

function detectPlatform() {
  if (typeof navigator === "undefined") return "desktop";
  const ua = navigator.userAgent || "";
  if (/iPhone|iPad|iPod/i.test(ua) && !/CriOS|FxiOS/.test(ua)) return "ios";
  if (/Android/i.test(ua)) return "android";
  if (/Macintosh|Windows|Linux/i.test(ua)) return "desktop";
  return "desktop";
}

function dismissedRecently() {
  try {
    const v = localStorage.getItem(DISMISS_KEY);
    if (!v) return false;
    const ts = parseInt(v, 10);
    if (!ts) return false;
    return Date.now() - ts < DISMISS_DAYS * 24 * 60 * 60 * 1000;
  } catch (_) { return false; }
}

export default function InstallAppButton({ variant = "ghost", size = "sm" }) {
  const [deferred, setDeferred] = useState(null);
  const [open, setOpen] = useState(false);
  const [platform, setPlatform] = useState("desktop");
  const [standalone, setStandalone] = useState(false);

  useEffect(() => {
    setPlatform(detectPlatform());
    setStandalone(isStandalone());

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferred(e);
    };
    const onInstalled = () => {
      setDeferred(null);
      setStandalone(true);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  // Auto-pop the prompt once after first login (respects 7-day dismissal)
  useEffect(() => {
    if (standalone) return;
    if (!deferred && platform !== "ios") return;
    if (dismissedRecently()) return;
    const t = setTimeout(() => setOpen(true), 4000);
    return () => clearTimeout(t);
  }, [deferred, platform, standalone]);

  // Already installed → don't show
  if (standalone) return null;

  const triggerNativePrompt = async () => {
    if (!deferred) { setOpen(true); return; }
    deferred.prompt();
    const { outcome } = await deferred.userChoice;
    if (outcome === "accepted") {
      setOpen(false);
      setDeferred(null);
      setStandalone(true);
    }
  };

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch (_) {}
    setOpen(false);
  };

  return (
    <>
      <Button
        type="button"
        variant={variant}
        size={size}
        onClick={() => (deferred ? triggerNativePrompt() : setOpen(true))}
        className="rounded-2xl tap text-primary hover:bg-primary-50 px-2 sm:px-3"
        data-testid="install-app-button"
        aria-label="Install app"
      >
        <Download className="h-4 w-4 sm:mr-1.5" />
        <span className="hidden sm:inline">Install app</span>
      </Button>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="right" className="w-full sm:max-w-md rounded-l-3xl p-0 overflow-y-auto" data-testid="install-app-sheet">
          <SheetHeader className="px-5 pt-5 pb-3 border-b border-border/60">
            <SheetTitle className="font-display text-2xl font-extrabold flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-primary" /> Install RENVIRO
            </SheetTitle>
            <SheetDescription className="text-xs text-muted-foreground">
              Add the app to your home screen / desktop. Faster open, push notifications, full-screen view, works offline.
            </SheetDescription>
          </SheetHeader>

          <div className="px-5 pt-4">
            <Tabs defaultValue={platform === "desktop" ? "desktop" : "mobile"}>
              <TabsList className="rounded-2xl">
                <TabsTrigger value="mobile" className="rounded-xl text-xs"><Smartphone className="h-3 w-3 mr-1" />Mobile</TabsTrigger>
                <TabsTrigger value="desktop" className="rounded-xl text-xs"><Monitor className="h-3 w-3 mr-1" />Desktop</TabsTrigger>
              </TabsList>

              <TabsContent value="mobile" className="mt-4 space-y-4">
                {deferred && platform === "android" ? (
                  <div className="rounded-2xl border border-primary/40 bg-primary-50 p-4">
                    <p className="text-sm font-semibold text-primary-900">One-tap install available</p>
                    <p className="text-xs text-muted-foreground mt-1">Your browser detected RENVIRO is installable.</p>
                    <Button onClick={triggerNativePrompt} className="rounded-2xl tap mt-3 w-full h-11" data-testid="install-native-android">
                      <Download className="h-4 w-4 mr-1.5" /> Install now
                    </Button>
                  </div>
                ) : null}

                {/* iOS instructions */}
                <div className="rounded-2xl border border-border/60 p-4">
                  <p className="text-sm font-semibold flex items-center gap-2"><Smartphone className="h-4 w-4 text-muted-foreground" /> iPhone / iPad (Safari)</p>
                  <ol className="text-xs text-muted-foreground mt-2 space-y-1.5 list-decimal pl-5">
                    <li>Open this page in <strong>Safari</strong> (not Chrome).</li>
                    <li>Tap the <strong>Share</strong> icon <span className="inline-flex items-center"><Share className="h-3.5 w-3.5 inline mx-0.5" /></span> at the bottom of Safari.</li>
                    <li>Scroll and choose <strong>Add to Home Screen</strong> <span className="inline-flex items-center"><Plus className="h-3.5 w-3.5 inline mx-0.5" /></span>.</li>
                    <li>Tap <strong>Add</strong> in the top-right.</li>
                  </ol>
                </div>

                {/* Android Chrome (also other browsers) */}
                <div className="rounded-2xl border border-border/60 p-4">
                  <p className="text-sm font-semibold flex items-center gap-2"><Smartphone className="h-4 w-4 text-muted-foreground" /> Android (Chrome / Edge)</p>
                  <ol className="text-xs text-muted-foreground mt-2 space-y-1.5 list-decimal pl-5">
                    <li>Open the menu (⋮) in the top-right of Chrome.</li>
                    <li>Tap <strong>Install app</strong> (or <strong>Add to Home screen</strong>).</li>
                    <li>Confirm <strong>Install</strong>.</li>
                  </ol>
                </div>
              </TabsContent>

              <TabsContent value="desktop" className="mt-4 space-y-4">
                {deferred ? (
                  <div className="rounded-2xl border border-primary/40 bg-primary-50 p-4">
                    <p className="text-sm font-semibold text-primary-900">One-click install available</p>
                    <p className="text-xs text-muted-foreground mt-1">Your browser detected RENVIRO is installable.</p>
                    <Button onClick={triggerNativePrompt} className="rounded-2xl tap mt-3 w-full h-11" data-testid="install-native-desktop">
                      <Download className="h-4 w-4 mr-1.5" /> Install now
                    </Button>
                  </div>
                ) : (
                  <div className="rounded-2xl border border-border/60 p-4">
                    <p className="text-sm font-semibold flex items-center gap-2"><Monitor className="h-4 w-4 text-muted-foreground" /> Chrome / Edge / Brave</p>
                    <ol className="text-xs text-muted-foreground mt-2 space-y-1.5 list-decimal pl-5">
                      <li>Look for the install icon <span className="inline-flex items-center"><Download className="h-3.5 w-3.5 inline mx-0.5" /></span> in the right edge of the address bar.</li>
                      <li>Click it and confirm <strong>Install</strong>.</li>
                      <li>Or open the menu (⋮) → <strong>Cast, save and share</strong> → <strong>Install RENVIRO Sales Memory</strong>.</li>
                    </ol>
                    <p className="text-[11px] text-muted-foreground mt-2">If you don't see the install option, try a hard refresh (Cmd/Ctrl+Shift+R).</p>
                  </div>
                )}

                <div className="rounded-2xl border border-border/60 p-4">
                  <p className="text-sm font-semibold flex items-center gap-2"><Monitor className="h-4 w-4 text-muted-foreground" /> Safari (macOS 14+)</p>
                  <ol className="text-xs text-muted-foreground mt-2 space-y-1.5 list-decimal pl-5">
                    <li>From the menu bar, click <strong>File</strong> → <strong>Add to Dock…</strong></li>
                    <li>Confirm the name and click <strong>Add</strong>.</li>
                  </ol>
                </div>
              </TabsContent>
            </Tabs>

            <div className="mt-6 mb-5 grid grid-cols-2 gap-2">
              <Button variant="outline" className="rounded-2xl h-11 tap" onClick={dismiss} data-testid="install-dismiss-button">
                <X className="h-4 w-4 mr-1.5" /> Not now
              </Button>
              <Button variant="outline" className="rounded-2xl h-11 tap" onClick={() => window.open("https://web.dev/articles/install-criteria", "_blank", "noopener,noreferrer")}>
                <ExternalLink className="h-3.5 w-3.5 mr-1.5" /> Learn more
              </Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
